import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Play, BookOpen, ChevronRight, ChevronDown, ArrowLeft, Mic, Code, Image, TerminalSquare } from 'lucide-react';
import { examsApi, publicConfigApi } from '@/services/api';
import { QuotaBanner } from '@/payment';
import {
  trackExamView,
  trackExamTimeSpent,
  trackCategorySelect,
  trackPracticeStart,
} from '@/lib/track';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { ShareButton } from '@/components/ShareButton';
import { getContentLocale } from '@/lib/contentLocale';
import { ExamAffiliationNotice } from '@/components/ExamAffiliationNotice';
import { isHandsOnCert, hasReadyLabs } from '@/lib/k8s-sim/handsOn';
import { getLabsForCert } from '@/lib/k8s-sim/labs';

interface ApiCategory {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  questionCount?: number;
}

interface ApiExam {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  bgGradient: string;
  totalQuestions: number;
  duration: number;
  passingScore: number;
  hasOralQuestions: boolean;
  hasCodeQuestions: boolean;
  hasImageQuestions: boolean;
  categories: ApiCategory[];
}

export function ExamDetailPage() {
  const navigate = useNavigate();
  const { examId } = useParams<{ examId: string }>();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [exam, setExam] = useState<ApiExam | null>(null);
  const [loading, setLoading] = useState(true);
  // Combien de questions la simulation tire réellement (config globale, plafonnée
  // à 100 côté SimulationPage). L'examen a une grosse banque (ex. 1000) mais la
  // simulation n'en présente qu'un sous-ensemble dans le temps imparti.
  const [fullExamQuestionsCount, setFullExamQuestionsCount] = useState(50);
  const locale = getContentLocale();

  // Titre de l'onglet = nom de l'examen (dès qu'il est chargé).
  useDocumentTitle(exam?.name);

  useEffect(() => {
    if (!examId) { setLoading(false); return; }
    examsApi.getById(examId)
      .then(({ data }) => setExam(data))
      .catch(() => setExam(null))
      .finally(() => setLoading(false));
  }, [examId]);

  useEffect(() => {
    publicConfigApi.get()
      .then(({ data }) => { if (data.fullExamQuestionsCount) setFullExamQuestionsCount(data.fullExamQuestionsCount); })
      .catch(() => {});
  }, []);

  // Suivi GA4 : consultation de la fiche (→ examen le plus visionné) + temps
  // passé (émis en quittant la page). exam_view n'est loggé qu'une fois.
  const viewTracked = useRef(false);
  useEffect(() => {
    if (!exam) return;
    const { id, name } = exam;
    if (!viewTracked.current) {
      trackExamView(id, name);
      viewTracked.current = true;
    }
    const start = Date.now();
    return () => trackExamTimeSpent(id, name, (Date.now() - start) / 1000);
  }, [exam]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-app">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="flex items-center justify-center h-full bg-app">
        <div className="text-center px-4">
          <p className="text-lg font-medium text-fg-muted">
            {locale === 'en' ? 'Exam not found' : 'Examen introuvable'}
          </p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium">
            {locale === 'en' ? 'Back' : 'Retour'}
          </button>
        </div>
      </div>
    );
  }

  const gradient = exam.bgGradient || 'from-emerald-500 to-teal-600';

  // Certif « hands-on » (CKAD/CKA/CKS…) : on présente le lab pratique (terminal
  // kubectl simulé) au lieu du QCM. Le QCM reste en base, simplement non proposé.
  const handsOn = isHandsOnCert(exam.slug);
  const labsReady = hasReadyLabs(exam.slug);
  const handleStartLab = () => navigate(`/labs?cert=${exam.slug}`);
  // Examens de simulation (labs) de cette certif — listés comme les catégories.
  const handsOnLabs = handsOn ? getLabsForCert(exam.slug) : [];
  const handleOpenLab = (labId: string) => navigate(`/labs?cert=${exam.slug}&lab=${labId}`);

  // Doit correspondre EXACTEMENT à SimulationPage : min(config, banque, 100).
  const simQuestions = Math.min(fullExamQuestionsCount, exam.totalQuestions || fullExamQuestionsCount, 100);

  const handleStartSimulation = () => {
    // Le vrai `simulation_start` est émis sur la page simulation, au lancement
    // effectif du chrono (évite un double comptage).
    navigate(`/exam/${exam.id}/simulation`);
  };

  const handleStartPractice = (categoryId: string, difficulty?: string, questionCount?: number) => {
    trackPracticeStart(exam.id, exam.name, categoryId, questionCount);
    const params = new URLSearchParams();
    if (difficulty) params.append('difficulty', difficulty);
    if (questionCount) params.append('count', questionCount.toString());
    const queryString = params.toString() ? `?${params.toString()}` : '';
    navigate(`/exam/${exam.id}/practice/${categoryId}${queryString}`);
  };

  const handleCategoryClick = (category: ApiCategory) => {
    setExpandedCategory(prev => {
      const next = prev === category.id ? null : category.id;
      // Suivi GA4 : catégories les plus cliquées (uniquement à l'ouverture).
      if (next) trackCategorySelect(exam.id, category.id, category.name);
      return next;
    });
  };

  const badges = [
    exam.hasOralQuestions && { icon: <Mic className="w-3 h-3" />, label: 'Audio', cls: 'bg-purple-500/10 text-purple-300/80 border-purple-500/10' },
    exam.hasCodeQuestions && { icon: <Code className="w-3 h-3" />, label: 'Code', cls: 'bg-blue-500/10 text-blue-300/80 border-blue-500/10' },
    exam.hasImageQuestions && { icon: <Image className="w-3 h-3" />, label: 'Images', cls: 'bg-emerald-500/10 text-emerald-300/80 border-emerald-500/10' },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; cls: string }[];

  return (
    <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
      {/* Top bar */}
      <div className="px-4 sm:px-6 py-3 flex-shrink-0 flex items-center justify-between border-b border-line">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-fg-subtle hover:text-fg transition-colors group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm font-medium">{locale === 'en' ? 'Back' : 'Retour'}</span>
        </button>
        <ShareButton mode="exam" examName={exam.name} examId={exam.id} />
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Mode gratuit : essais restants du jour (rien si paiements OFF / abonné) */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-3 lg:pb-0 empty:hidden">
          <QuotaBanner />
        </div>
        {/* Desktop: side-by-side layout */}
        <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-[1fr_340px] lg:gap-6 lg:px-6 lg:py-6">

          {/* Left column / Main content */}
          <div>
            {/* Hero section */}
            <div className="relative overflow-hidden lg:rounded-2xl">
              <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-[0.08]`} />
              <div className="relative px-4 sm:px-6 py-6 sm:py-8 lg:px-8 lg:py-10">
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center text-3xl sm:text-4xl lg:text-5xl shadow-lg shadow-black/20 flex-shrink-0`}>
                    {exam.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-fg leading-tight">{exam.name}</h1>
                    <ExamAffiliationNotice />
                    <p className="text-sm sm:text-base text-fg-subtle mt-1.5 sm:mt-2 leading-relaxed line-clamp-3 lg:line-clamp-none">{exam.description}</p>

                    {badges.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {badges.map((b) => (
                          <span key={b.label} className={`inline-flex items-center gap-1 text-[10px] sm:text-xs px-2 py-0.5 rounded-md border ${b.cls}`}>
                            {b.icon} {b.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Les détails (questions / durée / seuil) sont affichés sur l'écran
                de simulation — inutile de les répéter ici. */}

            {/* Action buttons - mobile only */}
            <div className="px-4 sm:px-6 mt-5 space-y-2.5 lg:hidden">
              {handsOn ? (
                <button
                  onClick={handleStartLab}
                  className={`w-full bg-gradient-to-r ${gradient} text-white py-4 rounded-2xl font-semibold text-base flex items-center justify-center gap-2.5 hover:opacity-90 transition-all active:scale-[0.98] shadow-lg shadow-black/20`}
                >
                  <TerminalSquare className="w-5 h-5" />
                  {labsReady
                    ? (locale === 'en' ? 'Start hands-on lab' : 'Passer le lab pratique')
                    : (locale === 'en' ? 'Labs coming soon' : 'Labs en préparation')}
                </button>
              ) : (
                <>
                  <button
                    onClick={handleStartSimulation}
                    className={`w-full bg-gradient-to-r ${gradient} text-white py-4 rounded-2xl font-semibold text-base flex items-center justify-center gap-2.5 hover:opacity-90 transition-all active:scale-[0.98] shadow-lg shadow-black/20`}
                  >
                    <Play className="w-5 h-5" />
                    {locale === 'en' ? 'Start simulation' : 'Lancer la simulation'}
                  </button>
                  <button
                    onClick={() => document.getElementById('categories-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className="w-full py-3.5 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 bg-surface border border-line text-fg-muted hover:text-fg hover:border-green-500/30 transition-all active:scale-[0.98]"
                  >
                    <BookOpen className="w-4 h-4" />
                    {locale === 'en' ? 'Or practice by category' : 'Ou pratiquer par catégorie'}
                  </button>
                </>
              )}
            </div>

            {/* Certif hands-on : liste des examens de simulation (labs) */}
            {handsOn && (
              <div className="px-4 sm:px-6 lg:px-0 mt-6 sm:mt-8 pb-6">
                {/* Liste des examens de simulation — comme les catégories des QCM */}
                <div>
                  <div className="flex items-center gap-2">
                    <TerminalSquare className="w-4 h-4 text-fg-faint" />
                    <h2 className="text-sm font-semibold text-fg-subtle uppercase tracking-wider">
                      {locale === 'en' ? 'Simulation exams' : 'Examens de simulation'}
                    </h2>
                    <span className="text-xs text-fg-faint ml-auto">{handsOnLabs.length}</span>
                  </div>
                  <p className="text-xs text-fg-faint mt-1 mb-3 sm:mb-4 leading-relaxed">
                    {locale === 'en'
                      ? 'Full killer.sh-style sessions: each exam bundles several graded questions to solve in the simulated terminal.'
                      : "Sessions complètes façon killer.sh : chaque examen regroupe plusieurs questions notées à résoudre dans le terminal simulé."}
                  </p>

                  {!labsReady || handsOnLabs.length === 0 ? (
                    <div className="rounded-2xl border border-line bg-surface p-5 text-sm text-fg-subtle">
                      {locale === 'en' ? 'Labs coming soon for this certification.' : 'Labs en préparation pour cette certification.'}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {handsOnLabs.map((lab) => (
                        <button
                          key={lab.id}
                          onClick={() => handleOpenLab(lab.id)}
                          className="w-full text-left rounded-2xl border border-line bg-surface p-4 hover:border-green-500/40 hover:bg-green-500/[0.04] transition-all active:scale-[0.99] flex items-center gap-3"
                        >
                          <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0">
                            <TerminalSquare className="w-5 h-5 text-green-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-semibold text-fg truncate">{lab.title}</h3>
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 flex-shrink-0">{lab.level}</span>
                            </div>
                            <p className="text-xs text-fg-subtle mt-0.5 line-clamp-2 leading-relaxed">{lab.summary}</p>
                            <p className="text-[11px] text-fg-faint mt-1">
                              {lab.tasks.length} {locale === 'en' ? 'questions' : 'questions'}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-fg-faint flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Categories (QCM) — masqué pour les certifs hands-on */}
            {!handsOn && (
            <div id="categories-section" className="px-4 sm:px-6 lg:px-0 mt-6 sm:mt-8 pb-6 scroll-mt-4">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-fg-faint" />
                <h2 className="text-sm font-semibold text-fg-subtle uppercase tracking-wider">
                  {locale === 'en' ? 'Practice by category' : 'Pratiquer par catégorie'}
                </h2>
                <span className="text-xs text-fg-faint ml-auto">{exam.categories.length}</span>
              </div>
              <p className="text-xs text-fg-faint mt-1 mb-3 sm:mb-4 leading-relaxed">
                {locale === 'en'
                  ? 'Train on one topic at a time, at your own pace — pick a category and the number of questions.'
                  : 'Entraînez-vous thème par thème, à votre rythme — choisissez une catégorie et le nombre de questions.'}
              </p>

              <div className="space-y-2">
                {exam.categories.map((category) => (
                  <CategoryCard
                    key={category.id}
                    category={category}
                    onClick={handleCategoryClick}
                    onStartPractice={handleStartPractice}
                    expanded={expandedCategory === category.id}
                    locale={locale}
                  />
                ))}
              </div>
            </div>
            )}
          </div>

          {/* Right column - desktop sticky sidebar */}
          <div className="hidden lg:block">
            <div className="sticky top-6 space-y-4">
              {/* Start card : lab (hands-on) ou simulation QCM */}
              {handsOn ? (
                <div className="bg-surface rounded-2xl border border-green-500/30 p-5">
                  <h3 className="text-sm font-semibold text-fg mb-1 flex items-center gap-2">
                    <TerminalSquare className="w-4 h-4 text-green-400" />
                    {locale === 'en' ? 'Hands-on lab' : 'Lab pratique'}
                  </h3>
                  <p className="text-xs text-fg-faint mb-4 leading-relaxed">
                    {locale === 'en'
                      ? 'Performance-based exam: solve real tasks in a simulated kubectl terminal, validated in real time.'
                      : "Examen pratique : résous de vraies tâches dans un terminal kubectl simulé, validé en temps réel."}
                  </p>
                  <button
                    onClick={handleStartLab}
                    className={`w-full bg-gradient-to-r ${gradient} text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-all active:scale-[0.98] shadow-lg shadow-black/20`}
                  >
                    <TerminalSquare className="w-4 h-4" />
                    {labsReady
                      ? (locale === 'en' ? 'Start hands-on lab' : 'Passer le lab pratique')
                      : (locale === 'en' ? 'Labs coming soon' : 'Labs en préparation')}
                  </button>
                </div>
              ) : (
              <div className="bg-surface rounded-2xl border border-line p-5">
                <h3 className="text-sm font-semibold text-fg mb-1">
                  {locale === 'en' ? 'Simulation exam' : 'Examen de simulation'}
                </h3>
                <p className="text-xs text-fg-faint mb-4 leading-relaxed">
                  {locale === 'en'
                    ? `${simQuestions} questions drawn at random in ${exam.duration} minutes. Pass mark: ${exam.passingScore}%.`
                    : `${simQuestions} questions tirées au hasard, en ${exam.duration} minutes. Seuil de réussite : ${exam.passingScore}%.`
                  }
                </p>
                <button
                  onClick={handleStartSimulation}
                  className={`w-full bg-gradient-to-r ${gradient} text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-all active:scale-[0.98] shadow-lg shadow-black/20`}
                >
                  <Play className="w-4 h-4" />
                  {locale === 'en' ? 'Start simulation' : 'Lancer la simulation'}
                </button>
                <p className="text-[11px] text-fg-faint mt-3 pt-3 border-t border-line leading-relaxed flex items-start gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 mt-px flex-shrink-0" />
                  {locale === 'en'
                    ? 'Not ready for the full exam? Practice one category at a time from the list.'
                    : "Pas encore prêt pour l'examen complet ? Pratiquez catégorie par catégorie depuis la liste."}
                </p>
              </div>
              )}

              {/* Quick info */}
              <div className="bg-surface rounded-2xl border border-line p-5 space-y-3">
                <h3 className="text-sm font-semibold text-fg mb-2">
                  {locale === 'en' ? 'Exam info' : 'Infos examen'}
                </h3>
                <InfoRow label={locale === 'en' ? 'Categories' : 'Catégories'} value={`${exam.categories.length}`} />
                <InfoRow label={locale === 'en' ? 'Simulation questions' : 'Questions (simulation)'} value={`${simQuestions}`} />
                <InfoRow label={locale === 'en' ? 'Question bank' : 'Banque de questions'} value={`${exam.totalQuestions}`} />
                <InfoRow label={locale === 'en' ? 'Duration' : 'Durée'} value={`${exam.duration} min`} />
                <InfoRow label={locale === 'en' ? 'Pass mark' : 'Seuil'} value={`${exam.passingScore}%`} />
                {badges.length > 0 && (
                  <div className="pt-2 border-t border-line">
                    <p className="text-[10px] text-fg-faint mb-1.5">{locale === 'en' ? 'Features' : 'Fonctionnalités'}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {badges.map((b) => (
                        <span key={b.label} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md border ${b.cls}`}>
                          {b.icon} {b.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-fg-subtle">{label}</span>
      <span className="text-xs font-medium text-fg-muted">{value}</span>
    </div>
  );
}

interface CategoryCardProps {
  category: ApiCategory;
  onClick: (category: ApiCategory) => void;
  onStartPractice: (categoryId: string, difficulty?: string, questionCount?: number) => void;
  expanded?: boolean;
  locale: 'fr' | 'en';
}

function CategoryCard({ category, onClick, onStartPractice, expanded, locale }: CategoryCardProps) {
  const getQuestionOptions = () => {
    const maxQuestions = category.questionCount || 0;
    if (maxQuestions <= 0) return [] as { value: number; label: string }[];
    // Paliers ADAPTATIFS : on ne propose que des tailles réalisables, et on
    // garantit toujours au moins une option (« toutes ») pour ne jamais bloquer
    // une catégorie qui a peu de questions.
    const tiers = [10, 20, 30, 50, 100].filter((v) => v <= maxQuestions);
    if (tiers.length === 0 || tiers[tiers.length - 1] < maxQuestions) {
      tiers.push(maxQuestions);
    }
    const uniq = [...new Set(tiers)].sort((a, b) => a - b);
    return uniq.map((v) => ({
      value: v,
      label: v === maxQuestions ? `${v} questions (toutes)` : `${v} questions`,
    }));
  };

  const availableOptions = getQuestionOptions();
  const [questionCount, setQuestionCount] = useState(availableOptions[0]?.value ?? 30);

  useEffect(() => {
    if (availableOptions.length > 0 && !availableOptions.some((o) => o.value === questionCount)) {
      setQuestionCount(availableOptions[0].value);
    }
  }, [category.questionCount]);

  const handleStart = () => {
    onStartPractice(category.id, undefined, questionCount);
  };

  const qCount = category.questionCount ?? 0;

  return (
    <div className={`bg-surface rounded-xl border transition-colors ${
      expanded ? 'border-green-500/20 bg-surface-2' : 'border-line hover:border-line'
    }`}>
      <div
        onClick={() => onClick(category)}
        className="p-3.5 sm:p-4 flex items-center gap-3 cursor-pointer"
      >
        <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-emerald-500/20 to-teal-600/20 rounded-xl flex items-center justify-center text-lg sm:text-xl flex-shrink-0 border border-emerald-500/10">
          {category.icon || '📖'}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-fg truncate">{category.name}</h3>
          {category.description && (
            <p className="text-[10px] sm:text-xs text-fg-faint mt-0.5 line-clamp-1">{category.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <span className="hidden sm:inline text-[10px] sm:text-xs text-fg-faint tabular-nums">{qCount} Q</span>
          <button
            onClick={(e) => { e.stopPropagation(); onClick(category); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              expanded
                ? 'bg-green-500/15 text-green-500 border-green-500/40'
                : 'bg-green-500/10 text-green-500 border-green-500/25 hover:bg-green-500/20 hover:border-green-500/40'
            }`}
          >
            <Play className="w-3 h-3" />
            {locale === 'en' ? 'Practice' : 'Pratiquer'}
          </button>
          {expanded
            ? <ChevronDown className="w-4 h-4 text-green-400 transition-transform" />
            : <ChevronRight className="w-4 h-4 text-fg-faint transition-transform" />
          }
        </div>
      </div>

      {expanded && (
        <div className="px-3.5 sm:px-4 pb-3.5 sm:pb-4 border-t border-line">
          {availableOptions.length === 0 ? (
            <p className="text-xs text-fg-subtle py-3">
              {locale === 'en' ? 'No questions available.' : 'Aucune question disponible dans cette catégorie.'}
            </p>
          ) : (
            <div className="pt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
              <select
                value={questionCount}
                onChange={(e) => setQuestionCount(Number(e.target.value))}
                className="select-dark flex-1 px-3 py-2.5 bg-app text-fg rounded-lg border border-line focus:outline-none focus:border-green-500/40 text-sm cursor-pointer"
              >
                {availableOptions.map(option => (
                  <option key={option.value} value={option.value} className="bg-surface text-fg">
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                onClick={handleStart}
                className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold text-xs sm:text-sm flex items-center justify-center gap-1.5 hover:from-green-700 hover:to-green-800 transition-all active:scale-[0.98] whitespace-nowrap"
              >
                <Play className="w-3.5 h-3.5" />
                {locale === 'en' ? 'Start practice' : 'Commencer'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
